import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Alert, Card, Typography, Space, Divider, Select, Tag, Upload } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { UploadOutlined } from '@ant-design/icons';
import Editor from '../other/Editor';
import { predefinedTags } from '../../assets/data/predefined-tags';
import { createBlog, clearError, clearSuccess } from '../../features/blog/blogSlice';
import {
  EditOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CreateBlogForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { creating, success, error } = useSelector((state) => state.blogs);
  
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [wordCount, setWordCount] = useState(0);
  const [fileList, setFileList] = useState([]);

  // Clear success/error messages on component mount
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  // Handle success - reset form and show notification
  useEffect(() => {
    if (success) {
      form.resetFields();
      setContent('');
      setTags([]);
      setWordCount(0);
      setFileList([]);
      
      // Clear success after showing
      setTimeout(() => {
        dispatch(clearSuccess());
      }, 3000);
    }
  }, [success, form, dispatch]);

  // Count words in content
  const countWords = (html) => {
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text ? text.split(/\s+/).length : 0;
  };

  // Handle content change
  const handleContentChange = (value) => {
    setContent(value);
    setWordCount(countWords(value));
  };

  // Handle file upload
  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // Handle form submission
  const handleFormSubmit = async (values) => {
    try {
      const formData = new FormData();
      
      // Append form fields
      formData.append('title', values.title);
      formData.append('summary', values.summary);
      formData.append('content', content);
      formData.append('category', values.category);
      formData.append('status', values.status || 'published');
      
      // Append tags as individual items (NOT JSON.stringify)
      if (tags.length > 0) {
        tags.forEach(tag => {
          formData.append('tags', tag);
        });
      }
      
      // Append featured image if selected
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('featuredImage', fileList[0].originFileObj);
      }

      dispatch(createBlog(formData));
    } catch (err) {
      console.error('Error creating blog:', err);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-bg-primary">
      {/* Header Section */}
      <div className="mb-8 text-center">
        <Space direction="vertical" size="small">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <EditOutlined className="text-2xl text-accent-blue" />
            <Title level={2} className="m-0 text-text-primary font-semibold">
              Create New Blog Post
            </Title>
          </div>
          <Text className="text-text-secondary text-base">
            Share your thoughts, insights, and expertise with your audience
          </Text>
        </Space>
      </div>

      {/* Alert Messages */}
      {success && (
        <Alert
          className="mb-6 border border-accent-green bg-green-50 rounded-lg"
          message="Blog post created successfully!"
          description="Your blog post has been published and is now visible to your audience."
          type="success"
          showIcon
          closable
          onClose={() => dispatch(clearSuccess())}
        />
      )}
      
      {error && (
        <Alert
          className="mb-6 border border-accent-red bg-red-50 rounded-lg"
          message="Creation Failed"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => dispatch(clearError())}
        />
      )}

      {/* Main Form Card */}
      <Card 
        className="shadow-medium border border-border-light rounded-xl bg-white"
        bodyStyle={{ padding: '32px' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          onFinishFailed={onFinishFailed}
          size="large"
          className="space-y-6"
        >
          {/* Basic Information Section */}
          <div className="mb-8">
            <Title level={4} className="text-text-primary mb-4 flex items-center">
              <CheckCircleOutlined className="mr-2 text-accent-blue" />
              Blog Information
            </Title>
            
            <Form.Item 
              label={<span className="text-text-primary font-medium">Blog Title</span>}
              name="title" 
              rules={[{ required: true, message: 'Please enter a blog title' }]}
              className="mb-6"
            >
              <Input 
                placeholder="Enter an engaging title for your blog post"
                className="h-12 border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue"
              />
            </Form.Item>

            <Form.Item 
              label={<span className="text-text-primary font-medium">Blog Summary</span>}
              name="summary" 
              rules={[{ required: true, message: 'Please enter a summary' }]}
              className="mb-6"
            >
              <TextArea 
                rows={3}
                placeholder="Write a compelling summary that will appear in blog previews..."
                className="border border-border-light rounded-lg hover:border-accent-blue focus:border-accent-blue resize-none"
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Form.Item 
                label={<span className="text-text-primary font-medium">Category</span>}
                name="category"
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select 
                  placeholder="Select blog category"
                  className="h-12"
                >
                  <Select.Option value="technology">💻 Technology</Select.Option>
                  <Select.Option value="design">🎨 Design</Select.Option>
                  <Select.Option value="development">⚡ Development</Select.Option>
                  <Select.Option value="career">🚀 Career</Select.Option>
                  <Select.Option value="tutorial">📚 Tutorial</Select.Option>
                  <Select.Option value="personal">👤 Personal</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item 
                label={<span className="text-text-primary font-medium">Status</span>}
                name="status"
                initialValue="published"
              >
                <Select 
                  placeholder="Select status"
                  className="h-12"
                >
                  <Select.Option value="draft">📝 Draft</Select.Option>
                  <Select.Option value="published">✅ Published</Select.Option>
                </Select>
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Form.Item 
                label={<span className="text-text-primary font-medium">Tags</span>}
                name="blogTags"
              >
                <Select
                  mode="multiple"
                  placeholder="Select or add tags"
                  value={tags}
                  onChange={setTags}
                  className="min-h-12"
                  options={predefinedTags.map(tag => ({ label: tag, value: tag }))}
                />
              </Form.Item>

              <Form.Item 
                label={<span className="text-text-primary font-medium">Featured Image</span>}
                name="featuredImage"
              >
                <Upload
                  fileList={fileList}
                  onChange={handleFileChange}
                  beforeUpload={() => false} // Prevent auto upload
                  maxCount={1}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />}>Select Image</Button>
                </Upload>
              </Form.Item>
            </div>
          </div>

          <Divider className="border-border-light" />

         {/* Content Editor Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <Title level={4} className="text-text-primary mb-0 flex items-center">
                <FileTextOutlined className="mr-2 text-accent-blue" />
                Blog Content
              </Title>
              <div className="flex items-center space-x-4">
                <Text className="text-text-secondary text-sm">
                  {wordCount > 0 && (
                    <>
                      <strong>{wordCount}</strong> words • 
                      <span className="text-accent-blue">
                        ~{Math.ceil(wordCount / 200)} min read
                      </span>
                    </>
                  )}
                </Text>
              </div>
            </div>
            
            {/* Using the new Editor component with sticky toolbar and fixed height */}
            <div className="editor-container border border-gray-200 rounded-lg" style={{ height: '700px', display: 'flex', flexDirection: 'column' }}>
              <Editor
                value={content}
                onChange={handleContentChange}
                placeholder="Write your blog content here... Share your insights, experiences, and knowledge!"
                minHeight="400px"
                stickyToolbar={true}
                style={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                editorStyle={{
                  height: 'calc(100% - 60px)', // Subtract toolbar height
                  overflow: 'auto',
                  border: 'none'
                }}
                toolbarStyle={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'white',
                  borderBottom: '1px solid #e8e8e8',
                  borderRadius: '8px 8px 0 0'
                }}
                onClear={() => setContent('')}
              />
            </div>

            {/* Media Rendering Guide Section */}
            <Card className="mt-4 bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
              <div className="mb-3">
                <Title level={5} className="mb-2 text-blue-900 flex items-center">
                  <FileTextOutlined className="mr-2" />
                  Media Rendering Guide
                </Title>
                <Text className="text-blue-700 text-sm">
                  Enhance your blog posts with rich media content
                </Text>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Regular Media */}
                <div className="bg-white p-4 rounded-lg border border-blue-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <Text strong className="text-green-700">Regular Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Single images/videos display as centered blocks
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Just paste the URL directly in text:<br/>
                    <span className="text-blue-600">https://example.com/screenshot.jpg</span>
                  </div>
                </div>

                {/* Carousel Media */}
                <div className="bg-white p-4 rounded-lg border border-blue-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    <Text strong className="text-purple-700">Carousel Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Multiple media in horizontal scrollable layout
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Create a list with media URLs:<br/>
                    • <span className="text-blue-600">https://tutorial1.jpg</span><br/>
                    • <span className="text-blue-600">https://tutorial2.jpg</span><br/>
                    • <span className="text-blue-600">https://demo.mp4</span>
                  </div>
                </div>

                {/* Grid Media */}
                <div className="bg-white p-4 rounded-lg border border-blue-100">
                  <div className="flex items-center mb-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                    <Text strong className="text-orange-700">Grid Media</Text>
                  </div>
                  <Text className="text-sm text-gray-600 mb-3">
                    Multiple media in organized grid layout
                  </Text>
                  <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                    Start with layout config line:<br/>
                    • <span className="text-orange-600">layout=[grid] col=[2]</span><br/>
                    • <span className="text-blue-600">https://code1.jpg</span><br/>
                    • <span className="text-blue-600">https://code2.jpg</span><br/>
                    • <span className="text-blue-600">https://result.jpg</span><br/>
                    • <span className="text-blue-600">https://final.jpg</span>
                  </div>
                </div>
              </div>

              {/* Supported Formats */}
              <div className="mt-4 p-3 bg-white rounded-lg border border-blue-100">
                <Text strong className="text-blue-900 block mb-2">Supported Formats:</Text>
                <div className="flex flex-wrap gap-2">
                  <Tag color="green">Images: .jpg, .png, .gif, .webp, .svg</Tag>
                  <Tag color="blue">Videos: .mp4, .webm, .ogg, .mov</Tag>
                  <Tag color="red">YouTube: youtube.com or youtu.be links</Tag>
                </div>
              </div>

              {/* Blog-Specific Pro Tips */}
              <div className="mt-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                <Text strong className="text-cyan-800 block mb-2">📝 Blog Pro Tips:</Text>
                <ul className="text-sm text-cyan-700 space-y-1 ml-4">
                  <li>• Use regular media for featured images and key illustrations</li>
                  <li>• Create step-by-step tutorials with carousel layouts</li>
                  <li>• Add descriptive captions before media URLs for context</li>
                  <li>• For comparisons: "layout=[grid] col=[2]" creates perfect before/after</li>
                  <li>• Mix screenshots, code snippets, and demo videos effectively</li>
                  <li>• Use grids to showcase multiple examples or variations</li>
                  <li>• Lists with 4+ media items automatically get navigation arrows</li>
                  <li>• All layouts are mobile-responsive and stack on small screens</li>
                  <li>• Perfect for technical tutorials and visual storytelling</li>
                </ul>
              </div>
            </Card>
          </div>

          {/* Tags Display */}
          {tags.length > 0 && (
            <div className="mb-6">
              <Text className="text-text-primary font-medium text-sm uppercase tracking-wide block mb-3">
                Selected Tags
              </Text>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, index) => (
                  <Tag 
                    key={index}
                    color="blue"
                    className="px-3 py-1 text-sm"
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          <Divider className="border-border-light" />

          {/* Writing Tips */}
          <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Text className="text-text-secondary text-sm">
              <strong>✍️ Writing Tips:</strong>
              <br />
              • Start with a compelling hook in your introduction
              • Use subheadings to break up long sections
              • Include examples and personal experiences
              • End with a clear conclusion or call-to-action
              • Aim for 500+ words for better engagement
            </Text>
          </div>

          {/* Submit Section */}
          <div className="pt-6 border-t border-border-light">
            <div className="flex justify-center">
              <Button
                type="primary"
                htmlType="submit"
                loading={creating}
                size="large"
                icon={<SaveOutlined />}
                className="h-12 px-8 bg-accent-blue hover:bg-accent-blue/90 border-accent-blue hover:border-accent-blue/90 rounded-lg font-semibold shadow-soft hover:shadow-medium transition-all duration-200"
              >
                {creating ? 'Publishing...' : 'Publish Blog Post'}
              </Button>
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CreateBlogForm;